import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface WisdomVerse {
  sanskrit: string;
  transliteration: string;
  translation: string;
  source: string;
  theme: string;
}

// Curated wisdom verses from the Rig Veda
const wisdomVerses: WisdomVerse[] = [
  {
    sanskrit: 'एकं सद् विप्रा बहुधा वदन्ति',
    transliteration: 'Ekaṁ sad viprā bahudhā vadanti',
    translation: 'Truth is one, the wise call it by many names.',
    source: 'Mandala 1, Hymn 164, Verse 46',
    theme: 'Unity of Truth'
  },
  {
    sanskrit: 'आ नो भद्राः क्रतवो यन्तु विश्वतः',
    transliteration: 'Ā no bhadrāḥ kratavo yantu viśvataḥ',
    translation: 'Let noble thoughts come to us from every direction.',
    source: 'Mandala 1, Hymn 89, Verse 1',
    theme: 'Open-mindedness'
  },
  {
    sanskrit: 'तमसो मा ज्योतिर्गमय',
    transliteration: 'Tamaso mā jyotirgamaya',
    translation: 'Lead me from darkness to light.',
    source: 'Brihadaranyaka Upanishad 1.3.28',
    theme: 'Enlightenment'
  },
  {
    sanskrit: 'सत्यं वद धर्मं चर',
    transliteration: 'Satyaṁ vada dharmaṁ cara',
    translation: 'Speak truth, practice righteousness.',
    source: 'Taittiriya Upanishad 1.11',
    theme: 'Righteous Living'
  },
  {
    sanskrit: 'अग्निमीळे पुरोहितं यज्ञस्य देवमृत्विजम्',
    transliteration: 'Agnim īḷe purohitaṁ yajñasya devam ṛtvijam',
    translation: 'I praise Agni, the foremost priest, the divine minister of sacrifice.',
    source: 'Mandala 1, Hymn 1, Verse 1',
    theme: 'The First Verse'
  },
];

interface DailyWisdomProps {
  className?: string;
}

export const DailyWisdom: React.FC<DailyWisdomProps> = ({ className }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Set initial verse based on day of year (consistent daily verse)
  useEffect(() => {
    const dayOfYear = Math.floor(
      (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000
    );
    setCurrentIndex(dayOfYear % wisdomVerses.length);
  }, []);

  const navigateVerse = (direction: 'prev' | 'next') => {
    if (isAnimating) return;
    setIsAnimating(true);

    setCurrentIndex(prev => {
      if (direction === 'next') {
        return (prev + 1) % wisdomVerses.length;
      } else {
        return prev === 0 ? wisdomVerses.length - 1 : prev - 1;
      }
    });

    setTimeout(() => setIsAnimating(false), 300);
  };

  if (!isVisible) return null;

  const verse = wisdomVerses[currentIndex];

  return (
    <section
      className={cn(
        // Museum exhibit card styling
        "relative",
        "bg-white dark:bg-museum-950",
        "border border-museum-200 dark:border-museum-800",
        "rounded-museum",
        className
      )}
    >
      {/* Content */}
      <div className="px-8 py-10 sm:px-12 sm:py-12">
        {/* Museum Label Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <span className={cn(
              "font-mono font-medium",
              "text-[10px] uppercase tracking-[0.2em]",
              "text-museum-400 dark:text-museum-500"
            )}>
              Verse of the Day
            </span>
            <p className={cn(
              "font-body italic",
              "text-sm mt-1",
              "text-museum-500 dark:text-museum-400"
            )}>
              {verse.theme}
            </p>
          </div>

          <button
            className={cn(
              "p-1.5 -mr-1.5 -mt-1.5",
              "text-museum-400 hover:text-museum-600",
              "dark:text-museum-500 dark:hover:text-museum-300",
              "transition-colors duration-200"
            )}
            onClick={() => setIsVisible(false)}
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Verse Content - The "Artwork" */}
        <div className={cn(
          "transition-opacity duration-300",
          isAnimating ? "opacity-0" : "opacity-100"
        )}>
          {/* Sanskrit - Primary Display */}
          <blockquote className="mb-6">
            <p className={cn(
              "font-sanskrit",
              "text-2xl sm:text-3xl leading-relaxed",
              "text-museum-800 dark:text-museum-200",
              "text-center"
            )}>
              {verse.sanskrit}
            </p>
          </blockquote>

          {/* Transliteration */}
          <p className={cn(
            "font-body italic",
            "text-base",
            "text-museum-500 dark:text-museum-400",
            "text-center mb-4"
          )}>
            {verse.transliteration}
          </p>

          {/* Divider */}
          <div className="w-12 h-px bg-museum-200 dark:bg-museum-700 mx-auto mb-4" />

          {/* Translation */}
          <p className={cn(
            "font-display",
            "text-lg sm:text-xl",
            "text-museum-700 dark:text-museum-300",
            "text-center leading-relaxed"
          )}>
            "{verse.translation}"
          </p>

          {/* Source - Museum Attribution */}
          <p className={cn(
            "font-mono",
            "text-[10px] uppercase tracking-[0.15em]",
            "text-museum-400 dark:text-museum-500",
            "text-center mt-6"
          )}>
            {verse.source}
          </p>
        </div>

        {/* Navigation - Minimal */}
        <div className="flex items-center justify-center gap-6 mt-10">
          <button
            className={cn(
              "p-2",
              "text-museum-400 hover:text-museum-700",
              "dark:text-museum-500 dark:hover:text-museum-300",
              "transition-colors duration-200"
            )}
            onClick={() => navigateVerse('prev')}
            aria-label="Previous verse"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
          </button>

          {/* Minimal dot indicators */}
          <div className="flex items-center gap-2">
            {wisdomVerses.map((_, idx) => (
              <button
                key={idx}
                className={cn(
                  "transition-all duration-300",
                  idx === currentIndex
                    ? "w-6 h-0.5 bg-museum-900 dark:bg-museum-100"
                    : "w-1.5 h-1.5 rounded-full bg-museum-300 dark:bg-museum-600 hover:bg-museum-400 dark:hover:bg-museum-500"
                )}
                onClick={() => {
                  if (!isAnimating) {
                    setIsAnimating(true);
                    setCurrentIndex(idx);
                    setTimeout(() => setIsAnimating(false), 300);
                  }
                }}
                aria-label={`Go to verse ${idx + 1}`}
              />
            ))}
          </div>

          <button
            className={cn(
              "p-2",
              "text-museum-400 hover:text-museum-700",
              "dark:text-museum-500 dark:hover:text-museum-300",
              "transition-colors duration-200"
            )}
            onClick={() => navigateVerse('next')}
            aria-label="Next verse"
          >
            <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default DailyWisdom;
